import React from 'react';
import LegalLayout, { COMPANY, H2, P, UL } from './LegalLayout';

const Privacy: React.FC = () => (
  <LegalLayout
    slug="privacy"
    title="Privacy Policy"
    description="How 12and22 (SIVRA Inc.) collects, uses, and protects your personal data."
  >
    <P>
      This Privacy Policy explains how {COMPANY.legalName}, operating as {COMPANY.brand}, collects,
      uses, discloses, and safeguards your information when you use our Services. We are the data
      controller for the personal data described here.
    </P>

    <H2>1. Information we collect</H2>
    <UL>
      <li><strong>Account data:</strong> name, email, phone, company name, and password (stored hashed).</li>
      <li><strong>Billing data:</strong> billing address and payment records. Card details are processed
        by our payment processor (Stripe) and are not stored on our servers.</li>
      <li><strong>Service data:</strong> domains, hosting accounts, VPS instances, mailboxes, and related
        configuration you create.</li>
      <li><strong>Technical data:</strong> IP address, browser type, and log data collected automatically
        for security, fraud prevention, and service operation.</li>
      <li><strong>Support data:</strong> the contents of tickets and messages you send us.</li>
    </UL>

    <H2>2. How we use your information</H2>
    <UL>
      <li>To provide, provision, and maintain the Services you purchase.</li>
      <li>To process payments and send transactional email (receipts, verification, service notices).</li>
      <li>To provide support and respond to your requests.</li>
      <li>To secure the Services, prevent abuse, and comply with legal obligations.</li>
      <li>To send service-related and, where permitted, marketing communications (you can opt out of
        marketing at any time).</li>
    </UL>

    <H2>3. Legal bases (EEA/UK users)</H2>
    <P>
      Where the EU/UK GDPR applies, we process personal data on the bases of: performance of a contract
      (to deliver the Services), legitimate interests (security, service improvement, fraud prevention),
      consent (for optional marketing and non-essential cookies), and compliance with legal obligations.
    </P>

    <H2>4. Sharing and processors</H2>
    <P>
      We do not sell your personal data. We share it with service providers who process it on our
      behalf, including: Stripe (payments), Amazon Web Services (email delivery and storage
      infrastructure), and our server infrastructure providers. These processors are bound to protect
      your data and use it only to provide services to us. We may also disclose data when required by
      law or to protect our rights and users.
    </P>

    <H2>5. International transfers</H2>
    <P>
      We operate from the United States and use infrastructure that may process data in the United
      States and other countries. Where required, we rely on appropriate safeguards (such as Standard
      Contractual Clauses) for cross-border transfers.
    </P>

    <H2>6. Data retention</H2>
    <P>
      We retain account and billing records for as long as your account is active and thereafter as
      needed to comply with legal, tax, and accounting obligations. Hosted content is retained per the
      Terms and deleted after a reasonable period following termination.
    </P>

    <H2>7. Your rights</H2>
    <P>
      Depending on your location, you may have rights to access, correct, delete, restrict, or port
      your personal data, and to object to certain processing. EEA/UK users may lodge a complaint with
      a supervisory authority. To exercise your rights, contact{' '}
      <a href={`mailto:${COMPANY.privacyEmail}`} className="text-primary-navy underline">{COMPANY.privacyEmail}</a>.
    </P>

    <H2>8. Security</H2>
    <P>
      We use technical and organisational measures including encryption in transit (HTTPS), hashed
      passwords, access controls, and encrypted storage of sensitive credentials. No method of
      transmission or storage is completely secure, and we cannot guarantee absolute security.
    </P>

    <H2>9. Cookies</H2>
    <P>
      We use cookies and similar technologies as described in our{' '}
      <a href="/cookies" className="text-primary-navy underline">Cookie Policy</a>.
    </P>

    <H2>10. Children</H2>
    <P>
      The Services are not directed to children under 16, and we do not knowingly collect their data.
    </P>

    <H2>11. Changes and contact</H2>
    <P>
      We may update this Policy; material changes will be posted here with a new effective date.
      Contact us at{' '}
      <a href={`mailto:${COMPANY.privacyEmail}`} className="text-primary-navy underline">{COMPANY.privacyEmail}</a>{' '}
      or {COMPANY.legalName}, {COMPANY.address}.
    </P>
  </LegalLayout>
);

export default Privacy;
